import {
  ApiTokenListResponse,
  DetailedInvoiceDTO,
  InvoiceDTO,
  PaymentMethodDTO,
  UserMemberBean,
  UserMemberCommunicationPatchDTO,
  UserMemberProfileDTO,
  UserMemberSignupAsStaffDTO,
} from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
  queryOptions,
} from "@tanstack/react-query";

import { memberQueryKeys } from "../../../hooks";
import { api } from "../../api";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";

const keys = {
  all: () => ["staffApiMember"] as QueryKey,
  id: (userId: unknown) => [...keys.all(), userId] as QueryKey,
  idDeprecated: (userId: unknown) =>
    [...keys.id(userId), "deprecated"] as QueryKey,
  invoices: (userId: unknown) => [...keys.id(userId), "invoices"] as QueryKey,
  invoice: (userId: unknown, invoiceId?: unknown) =>
    [...keys.id(userId), "invoice", invoiceId] as QueryKey,
  paymentMethod: (userId: unknown, paymentMethodId?: unknown) =>
    [...keys.id(userId), "paymentMethod", paymentMethodId] as QueryKey,
  paymentMethods: (userId: unknown) =>
    [...keys.id(userId), "paymentMethods"] as QueryKey,
  invoiceFile: (userId: unknown, invoiceNumber?: unknown) =>
    [...keys.id(userId), "invoiceFile", invoiceNumber] as QueryKey,
};

export const staffApiMember = {
  keys,
  queries: {
    id: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.id(userId),
        queryFn: async () => {
          const result = (
            await api.get<UserMemberProfileDTO>(
              `/user-member-profile/${userId}`,
            )
          ).data;
          return {
            ...result,
            userMemberId: userId as string,
          } as UserMemberProfileDTO;
        },
        enabled: !!userId,
      }),
    /**
     * @deprecated - use .id() instead
     */
    idDeprecated: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.idDeprecated(userId),
        queryFn: async () => {
          return (await api.get<UserMemberBean>(`/user/member/${userId}`)).data;
        },
        enabled: !!userId,
      }),
    invoice: (userId: string | undefined, invoiceNumber: string | undefined) =>
      queryOptions({
        queryKey: keys.invoice(userId, invoiceNumber),
        queryFn: async () => {
          return (
            await api.get<DetailedInvoiceDTO>(`/invoice/${invoiceNumber}`)
          ).data;
        },
        enabled: !!userId && !!invoiceNumber,
      }),
    paymentMethod: (userId: string | undefined, paymentMethodId: string) =>
      queryOptions({
        queryKey: keys.paymentMethod(userId, paymentMethodId),
        queryFn: async () => {
          return (
            await api.get<PaymentMethodDTO>(
              `/payment-method/${userId}/${paymentMethodId}`,
            )
          ).data;
        },
        enabled: !!userId,
      }),
    invoiceFile: (
      userId: string | undefined,
      invoiceNumber: string | undefined,
    ) =>
      queryOptions({
        queryKey: keys.invoiceFile(userId, invoiceNumber),
        queryFn: async () => {
          return (
            await api.get<Blob>(`/invoice/${invoiceNumber}/pdf`, {
              responseType: "blob",
            })
          ).data;
        },
      }),
    paymentMethods: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.paymentMethods(userId),
        queryFn: async () => {
          return (
            await api.get<PaymentMethodDTO[]>(`/payment-method/${userId}`)
          ).data;
        },
        enabled: !!userId,
      }),
  },
  infiniteQueries: {
    invoices: (userId: string | undefined) =>
      infiniteQueryOptions({
        queryKey: keys.invoices(userId),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<InvoiceDTO>>(
              `/user-member-invoice-list/${userId}`,
              {
                params: {
                  size: 10,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        ...tokenPagination,
      }),
  },
  mutations: {
    create: () =>
      mutationOptions({
        mutationFn: async (data: UserMemberSignupAsStaffDTO) => {
          return (
            await api.post<{
              userMemberId: string;
            }>("/user-member", data)
          ).data;
        },
      }),
    updateProfile: () =>
      mutationOptions({
        mutationFn: async ({
          payload,
          userId,
        }: {
          payload: Partial<UserMemberProfileDTO>;
          userId: string;
        }) => {
          return (await api.put(`/user-member-profile/${userId}`, payload))
            .data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    updateProfilePicture: () =>
      mutationOptions({
        mutationFn: async ({
          picture: localUri,
          userId,
        }: {
          picture: string;
          userId: string;
        }) => {
          const response = await fetch(localUri);
          const blob = await response.blob();
          const formData = new FormData();
          formData.append("file", blob, "image.jpg");
          return (
            await api.post(`/user-member-profile/${userId}/picture`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    deleteAccount: () =>
      mutationOptions({
        mutationFn: async ({ userId }: { userId: string }) => {
          return (await api.post(`/user/member/${userId}/account-delete`)).data;
        },
      }),
    updateEmailAndPassword: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          params,
        }: {
          userId: string;
          params: { email?: string; password?: string };
        }) => (await api.patch(`/user/member/${userId}/account`, params)).data,
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    sendScaEmail: () =>
      mutationOptions({
        mutationFn: async ({ invoiceNumber }: { invoiceNumber: string }) => {
          return (await api.put(`/invoice/${invoiceNumber}/send-auth-email/`))
            .data;
        },
      }),
    sendAchVerificationEmail: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          paymentMethodId,
        }: {
          userId: string;
          paymentMethodId: string;
        }) => {
          return (
            await api.put(
              `/payment-method/${userId}/${paymentMethodId}/send-verify-email`,
            )
          ).data;
        },
      }),
    payOverdueInvoice: () =>
      mutationOptions({
        mutationFn: async ({
          invoiceNumber,
          paymentMethodId,
        }: {
          invoiceNumber: string;
          paymentMethodId: string;
          userId: string;
        }) => {
          return (
            await api.put(
              `/invoice/${invoiceNumber}/collect-past-due/${paymentMethodId}`,
              {
                paymentMethodId,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    updateCommunication: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          patch,
        }: {
          userId: string;
          patch: Partial<UserMemberCommunicationPatchDTO>;
        }) => {
          return (
            await api.patch(
              `/user-member-profile/${userId}/communication`,
              patch,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: keys.id(userId),
            }),
            queryClient.invalidateQueries({ queryKey: memberQueryKeys.all() }),
          ]);
        },
      }),
    assignTrainer: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          staffId,
        }: {
          userId: string;
          staffId: string;
        }) =>
          (await api.put(`/user/member/${userId}/assign-staff/${staffId}`))
            .data,
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    unassignTrainer: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          staffId,
        }: {
          userId: string;
          staffId: string;
        }) =>
          (await api.delete(`/user/member/${userId}/assign-staff/${staffId}`))
            .data,
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    updateHomeClub: () =>
      mutationOptions({
        mutationFn: async ({
          userId,
          clubId,
        }: {
          userId: string;
          clubId: number;
        }) => (await api.put(`/user/member/${userId}/new-club/${clubId}`)).data,
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
  },
  links: {
    scaRedirectUrl: (invoiceNumber: string) =>
      `${api.defaults.baseURL}/sca/invoice/${invoiceNumber}`,
    verifyPaymentMethod: (paymentMethodId: string) =>
      `${api.defaults.baseURL}/stripe-verify/${paymentMethodId}`,
  },
};
